// Centralized, locale-aware display formatters for canonical backend enum
// values (role keys, industry keys, organization types, membership types).
//
// These functions NEVER modify the canonical value used for permissions,
// routing, filtering, API calls, or persistence. They only map a canonical
// value to a stable English display label, which callers then localize by
// passing the result through the i18n `t()` function (English literals here
// are translation keys, not final copy). Unknown/future values fall back to a
// humanized form so the UI never crashes on an unrecognized enum.

export type Translator = (value: string) => string

// Humanize an unknown snake_case / kebab-case canonical value into a readable
// label, e.g. `regional_dispatch` -> `Regional Dispatch`. Used as the safe
// fallback for values not present in the maps below.
export const humanizeCanonicalValue = (value?: string | null): string => {
  if (!value) return ''
  return String(value)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

// Canonical role key -> stable English display label (also an i18n key).
const ROLE_DISPLAY_LABELS: Record<string, string> = {
  system_super_admin: 'System Super Admin',
  platform_admin: 'Platform Admin',
  general_systems_admin: 'Platform Admin',
  tenant_admin: 'Tenant Admin',
  company_admin: 'Company Admin',
  agency_admin: 'Agency Admin',
  supervisor: 'Supervisor',
  corporate_user: 'Corporate User',
  worker: 'Worker',
}

// Canonical industry key -> stable English display label (also an i18n key).
const INDUSTRY_DISPLAY_LABELS: Record<string, string> = {
  general_workforce: 'General Workforce Operations',
  staffing_agency: 'Staffing Agency',
  security_operations: 'Security Company',
  healthcare: 'Healthcare Provider',
  home_care: 'Home Care Agency',
  hospitality: 'Hospitality / Events',
  construction: 'Construction / Field Services',
  property_services: 'Property Services',
}

// Canonical organization type -> stable English display label.
const ORG_TYPE_DISPLAY_LABELS: Record<string, string> = {
  agency: 'Agency',
  company: 'Company',
  platform_owner: 'Platform Owner',
  organization: 'Organization',
}

// Canonical membership type -> stable English display label.
const MEMBERSHIP_TYPE_DISPLAY_LABELS: Record<string, string> = {
  worker: 'Worker',
  member: 'Member',
  admin: 'Admin',
  owner: 'Owner',
  corporate: 'Corporate User',
}

const resolveLabel = (map: Record<string, string>, canonical?: string | null): string => {
  if (!canonical) return ''
  return map[canonical] || humanizeCanonicalValue(canonical)
}

// English label resolvers (return an i18n key). Pass the result to `t()`.
export const roleDisplayLabel = (canonical?: string | null) => resolveLabel(ROLE_DISPLAY_LABELS, canonical)
export const industryDisplayLabel = (canonical?: string | null) => resolveLabel(INDUSTRY_DISPLAY_LABELS, canonical)
export const orgTypeDisplayLabel = (canonical?: string | null) => resolveLabel(ORG_TYPE_DISPLAY_LABELS, canonical)
export const membershipTypeDisplayLabel = (canonical?: string | null) => resolveLabel(MEMBERSHIP_TYPE_DISPLAY_LABELS, canonical)

// Convenience localized resolvers: canonical value -> localized display label.
export const localizedRoleLabel = (t: Translator, canonical?: string | null) => t(roleDisplayLabel(canonical))
export const localizedIndustryLabel = (t: Translator, canonical?: string | null) => t(industryDisplayLabel(canonical))
export const localizedOrgTypeLabel = (t: Translator, canonical?: string | null) => t(orgTypeDisplayLabel(canonical))
export const localizedMembershipTypeLabel = (t: Translator, canonical?: string | null) => t(membershipTypeDisplayLabel(canonical))
